import React, { useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  MessageCircle,
  ChefHat,
  Boxes,
  Truck,
  Sparkles,
  Building2,
  Wallet,
  Check,
  ArrowRight,
  Smartphone,
  BarChart3,
  Mail,
} from "lucide-react";
import "./BookingDetails.scss";
import { openExternalLink } from "../../../services/site/linkService";
import { InfoService } from "../../../services/site/InfoService";
import VideoPlayer from "../../../Components/VideoPlayer";
import homeVideo from "../../../assets/videos/home.mp4";
import BookingPricing from "./BookingPricing";

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};
const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const scaleIn = {
  hidden: { scale: 0.85, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } },
};

const AnimatedSection: React.FC<{
  children: React.ReactNode;
  className?: string;
  variant?: any;
}> = ({ children, className = "", variant = fadeInUp }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [controls, inView]);

  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={variant}
      className={className}
    >
      {children}
    </motion.section>
  );
};

const HeroSection: React.FC = () => {
  const tryFree = () => openExternalLink(InfoService?.bookingLink);
  return (
    <AnimatedSection className="sds_bk-hero section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="sds_bk-eyebrow">FLAGSHIP · WhatsApp-first</span>
              <h1 className="title is-1 sds_bk-hero-title">
                Run your restaurant from{" "}
                <span className="sds_bk-accent">WhatsApp</span>. Literally.
              </h1>
              <p className="subtitle is-5 sds_bk-hero-subtitle mb-5">
                Customers order on WhatsApp. Your kitchen sees it instantly.
                Your stock auto-deducts. Your vendor invoices auto-parse from
                Gmail. Your reports write themselves. All in one place.
              </p>
              <div className="sds_bk-hero-badges">
                <span className="tag is-large sds_bk-badge">
                  💬 WhatsApp Ordering
                </span>
                <span className="tag is-large sds_bk-badge">
                  👨‍🍳 Kitchen Display
                </span>
                <span className="tag is-large sds_bk-badge">
                  📦 Recipe Inventory
                </span>
                <span className="tag is-large sds_bk-badge">
                  🤖 AI Reports
                </span>
              </div>
              <div className="sds_bk-hero-buttons mt-5">
                <button className="button is-primary is-medium" onClick={tryFree}>
                  Start Free Trial <ArrowRight size={18} />
                </button>
                <button
                  className="button is-light is-medium"
                  onClick={() => openExternalLink(InfoService?.whatsappLink)}
                >
                  <MessageCircle size={18} /> Talk on WhatsApp
                </button>
              </div>
            </motion.div>
          </div>
          <div className="column is-6">
            <motion.div
              className="sds_bk-phone-mock"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <div className="sds_bk-phone">
                <div className="sds_bk-phone-header">
                  <div className="sds_bk-phone-avatar">🍽️</div>
                  <div>
                    <div className="sds_bk-phone-name">Hot &amp; Spicy Diner</div>
                    <div className="sds_bk-phone-status">online</div>
                  </div>
                </div>
                <div className="sds_bk-phone-body">
                  <div className="sds_bk-bubble in">Hi! 👋 Show me the menu</div>
                  <div className="sds_bk-bubble out">
                    Welcome! Here's today's menu 🍕🍔🥗
                    <br />Tap any item to add to cart.
                  </div>
                  <div className="sds_bk-bubble in">2x Margherita, 1x Coke</div>
                  <div className="sds_bk-bubble out">
                    Cart: ₹540 ·{" "}
                    <span className="sds_bk-link">Pay now »</span>
                  </div>
                  <div className="sds_bk-bubble in">Paid ✅</div>
                  <div className="sds_bk-bubble out">
                    Order #1042 confirmed. Kitchen prepping now ⏱️
                  </div>
                </div>
              </div>
              <div className="sds_bk-phone-glow"></div>
            </motion.div>
          </div>
        </div>

        <VideoPlayer
          videoSrc={homeVideo}
          title="See HDT Booking in 60 seconds"
          description="Watch a real order flow from WhatsApp to your kitchen, your stock, and your AI reports."
          lightBackground={false}
        />
      </div>
    </AnimatedSection>
  );
};

const ProblemSolutionSection: React.FC = () => {
  const rows = [
    {
      old: "Customer calls / DMs / uses 3 apps",
      hdt: "Single WhatsApp number, full menu, cart, payment",
    },
    {
      old: "POS, KOT, inventory, accounting all separate",
      hdt: "One unified backend",
    },
    {
      old: "Manager keys in vendor invoices manually",
      hdt: "Gmail auto-parses + flags overcharges",
    },
    {
      old: "Wastage = guesswork",
      hdt: "Daily prep + recipe-linked stock + closure log",
    },
    {
      old: "Reports = static dashboards",
      hdt: "Ask in plain English, AI answers",
    },
  ];

  return (
    <AnimatedSection className="sds_bk-compare section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_bk-section-title">
            The old way vs. the HDT way
          </h2>
          <p className="subtitle is-5">
            Five fewer tools. One platform. Half the staff time.
          </p>
        </div>
        <div className="sds_bk-compare-table">
          <div className="sds_bk-compare-row sds_bk-compare-head">
            <div>Old way</div>
            <div>HDT way</div>
          </div>
          {rows.map((r, i) => (
            <motion.div
              key={i}
              className="sds_bk-compare-row"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
            >
              <div className="sds_bk-old">{r.old}</div>
              <div className="sds_bk-new">
                <Check size={16} className="sds_bk-check" /> {r.hdt}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const CapabilitiesSection: React.FC = () => {
  const buckets = [
    {
      label: "Customer-Facing",
      color: "#25D366",
      icon: <Smartphone />,
      items: [
        "WhatsApp Ordering — conversational menu, cart, checkout, tracking. No app install.",
        "Combos & Offers — bundle products/variants with special pricing.",
        "Coupons & Campaigns — date-bound codes, broadcast WhatsApp campaigns.",
        "Table & Slot Booking — same engine for restaurant tables and salon stylists.",
        "Loyalty Points — earn/redeem rules per org.",
        "Multi-channel Payments — Razorpay, Stripe, Cashfree, PayPal, COD.",
      ],
    },
    {
      label: "Operations",
      color: "#FF9800",
      icon: <ChefHat />,
      items: [
        "Kitchen Display (KDS / KOT) — color-coded overdue alerts, audio, auto-assign chef.",
        "Order Management — accept/reject/dispatch, delivery board, WhatsApp status push.",
        "Staff & Delivery — chef/waiter/delivery dashboards, lifecycle tracking.",
        "Customer Database — Excel import/export, birthdays, segmentation.",
      ],
    },
    {
      label: "Back-of-House",
      color: "#9C27B0",
      icon: <Boxes />,
      items: [
        "Raw Materials & Recipes — bill-of-materials per dish with wastage %.",
        "Daily Prep & Closure — leftover/sold/wasted log; auto stock deduction on orders.",
        "Vendors & POs — quoted vs. last-supply prices, auto-PO from low stock.",
        "Invoice Pipeline — manual / Gmail / WhatsApp ingestion with discrepancy flags.",
        "Cost-per-dish + theoretical-vs-actual variance reports.",
      ],
    },
    {
      label: "Intelligence",
      color: "#2196F3",
      icon: <Sparkles />,
      items: [
        "Gmail → WhatsApp Approval — 5-min poll, OCR + GPT Vision parse, one-tap approve.",
        "AI Report Chat — ask 'top combo on weekends?' — agent answers from live DB.",
        "AI Helpers — recipe suggestions, HSN/GST codes, expense categorize, campaign writer.",
        "Self-improving — HSN cache reuses past lookups; cost drops over time.",
      ],
    },
  ];

  return (
    <AnimatedSection className="sds_bk-capabilities section sds_bk-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_bk-section-title">
            Everything a modern restaurant needs
          </h2>
          <p className="subtitle is-5">
            Front-of-house, kitchen, inventory, vendors, intelligence — one
            product, not five integrations.
          </p>
        </div>
        <motion.div
          className="columns is-multiline"
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.1 }}
        >
          {buckets.map((b, idx) => (
            <motion.div key={idx} className="column is-6" variants={scaleIn}>
              <motion.div
                className="sds_bk-cap-card"
                whileHover={{ y: -8 }}
                style={{ borderTopColor: b.color }}
              >
                <div
                  className="sds_bk-cap-icon"
                  style={{ background: b.color }}
                >
                  {b.icon}
                </div>
                <h3 className="title is-4 mb-3">{b.label}</h3>
                <ul className="sds_bk-cap-list">
                  {b.items.map((it, i) => (
                    <li key={i}>
                      <span
                        className="sds_bk-cap-check"
                        style={{ color: b.color }}
                      >
                        ✓
                      </span>
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </AnimatedSection>
  );
};

const AISpotlightSection: React.FC = () => {
  const cards = [
    {
      icon: <Mail />,
      title: "Invoice Auto-Parsing",
      color: "#2196F3",
      desc: "Connect Gmail. Every 5 minutes we OCR your vendor invoices and WhatsApp the manager a one-tap approve / reject — with price-variance vs. last purchase.",
    },
    {
      icon: <BarChart3 />,
      title: "AI Report Chat",
      color: "#9C27B0",
      desc: "Ask 'What was my food cost % last week?' or 'Top-selling combo on Sundays?' — the agent runs the queries and answers in plain English.",
    },
    {
      icon: <Sparkles />,
      title: "Recipe & HSN Suggestions",
      color: "#FF5722",
      desc: "Type a dish name, get an ingredient list. Add a product, get an HSN/GST code suggestion. AI helpers everywhere admin work used to be.",
    },
  ];

  return (
    <AnimatedSection className="sds_bk-ai section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_bk-section-title">
            🤖 AI built in, not bolted on
          </h2>
          <p className="subtitle is-5">
            Three places AI does the boring work for you.
          </p>
        </div>
        <div className="columns">
          {cards.map((c, i) => (
            <motion.div
              key={i}
              className="column"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
            >
              <div
                className="sds_bk-ai-card"
                style={{ borderTopColor: c.color }}
              >
                <div className="sds_bk-ai-icon" style={{ background: c.color }}>
                  {c.icon}
                </div>
                <h3 className="title is-4 mb-3">{c.title}</h3>
                <p className="has-text-grey">{c.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const IndustriesSection: React.FC = () => {
  const verticals = [
    {
      icon: "🍽️",
      title: "Restaurant",
      desc: "Menu, KOT, recipe-linked stock, dine-in / pickup / delivery, table reservations.",
    },
    {
      icon: "🥐",
      title: "Bakery",
      desc: "Daily prep batching, leftover/wastage closure, advance order slots.",
    },
    {
      icon: "💇",
      title: "Salon",
      desc: "Slot booking with stylist assignment, package combos, follow-up campaigns.",
    },
    {
      icon: "🛒",
      title: "Provisional / Grocery",
      desc: "Catalog with HSN auto-codes, vendor reorder flow, multi-rate GST handling.",
    },
    {
      icon: "👕",
      title: "Clothing Store",
      desc: "Variant matrix (size/color), HSN class, seasonal campaigns.",
    },
  ];

  return (
    <AnimatedSection className="sds_bk-industries section sds_bk-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_bk-section-title">
            Built for the businesses you run
          </h2>
          <p className="subtitle is-5">
            One platform, five verticals, zero retraining.
          </p>
        </div>
        <div className="columns is-multiline">
          {verticals.map((v, i) => (
            <motion.div
              key={i}
              className="column is-4"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
            >
              <div className="sds_bk-ind-card">
                <div className="sds_bk-ind-icon">{v.icon}</div>
                <h3 className="title is-5">{v.title}</h3>
                <p className="has-text-grey">{v.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const MigrationSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_bk-migrate section">
      <div className="container">
        <div className="columns is-vcentered">
          <div className="column is-6">
            <h2 className="title is-2 sds_bk-section-title">
              Migrate from your current POS in one upload
            </h2>
            <p className="subtitle is-5 has-text-grey">
              Square (Items Detail CSV), Clover (Orders CSV), and our own
              template. Auto-detect the format, fuzzy-match products, one-click
              import.
            </p>
            <ul className="sds_bk-migrate-list">
              <li>
                <Check className="sds_bk-check" /> Auto-detect Square / Clover
                exports
              </li>
              <li>
                <Check className="sds_bk-check" /> Fuzzy-match products to avoid
                duplicates
              </li>
              <li>
                <Check className="sds_bk-check" /> One-click bulk order &amp;
                catalog import
              </li>
              <li>
                <Check className="sds_bk-check" /> Switch in hours, not weeks
              </li>
            </ul>
          </div>
          <div className="column is-6 has-text-centered">
            <div className="sds_bk-migrate-logos">
              <div className="sds_bk-migrate-logo">Square</div>
              <ArrowRight size={32} className="sds_bk-migrate-arrow" />
              <div className="sds_bk-migrate-logo sds_bk-migrate-target">
                HDT
              </div>
              <ArrowRight size={32} className="sds_bk-migrate-arrow" />
              <div className="sds_bk-migrate-logo">Clover</div>
            </div>
          </div>
        </div>
      </div>
    </AnimatedSection>
  );
};

const PricingSection: React.FC = () => {
  return (
    <AnimatedSection className="sds_bk-pricing section sds_bk-bg-light">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_bk-section-title">
            Simple, scalable pricing
          </h2>
          <p className="subtitle is-5">
            Start small. Add modules as you grow. Cancel anytime.
          </p>
        </div>
        <BookingPricing />
      </div>
    </AnimatedSection>
  );
};

const FAQSection: React.FC = () => {
  const faqs = [
    {
      q: "Do my customers need an app?",
      a: "No. Everything happens on WhatsApp — the app they already have.",
    },
    {
      q: "Can I migrate from Square / Clover / Petpooja?",
      a: "Yes. Upload your export, we auto-detect the format and import catalog + orders.",
    },
    {
      q: "How does invoice auto-parsing work?",
      a: "Connect Gmail. We poll every 5 min, parse PDFs/images with OCR + AI, and WhatsApp your manager for one-tap approval.",
    },
    {
      q: "What if the AI gets a price wrong?",
      a: "Every parsed line shows price-variance % vs. last purchase; approval is human-in-the-loop.",
    },
    {
      q: "Do I need to set up recipes for every dish?",
      a: "No — use it where it matters (high-volume / high-cost). The system AI-suggests ingredients from product name.",
    },
    {
      q: "Is my data secure?",
      a: "Field-level AES-256-GCM encryption, JWT auth, OTP, role-based access, multi-tenant isolation.",
    },
    {
      q: "How are reports generated?",
      a: "Pre-built dashboards or just ask the AI ('top combo last weekend') and it queries the live DB.",
    },
    {
      q: "Can franchise partners use this?",
      a: "Yes. Built-in franchise dashboard, auto wallet credits on each subscription payment, payout flow.",
    },
  ];

  return (
    <AnimatedSection className="sds_bk-faq section">
      <div className="container">
        <div className="has-text-centered mb-6">
          <h2 className="title is-2 sds_bk-section-title">
            Frequently asked questions
          </h2>
        </div>
        <div className="sds_bk-faq-list">
          {faqs.map((f, i) => (
            <motion.details
              key={i}
              className="sds_bk-faq-item"
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <summary className="sds_bk-faq-q">{f.q}</summary>
              <div className="sds_bk-faq-a">{f.a}</div>
            </motion.details>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
};

const FinalCTA: React.FC = () => {
  return (
    <AnimatedSection className="sds_bk-finalcta section">
      <div className="container has-text-centered">
        <h2 className="title is-2 has-text-white mb-4">
          Ready to run your business from WhatsApp?
        </h2>
        <p className="subtitle is-5 has-text-white-ter mb-5">
          Book a 20-minute demo. We'll show you a real order flowing through
          ordering → KDS → stock → AI report.
        </p>
        <div className="sds_bk-finalcta-buttons">
          <button
            className="button is-medium sds_bk-cta-primary"
            onClick={() => openExternalLink(InfoService?.bookingLink)}
          >
            Start Free Trial
          </button>
          <button
            className="button is-medium sds_bk-cta-secondary"
            onClick={() => openExternalLink(InfoService?.whatsappLink)}
          >
            <MessageCircle size={18} /> Book a Demo on WhatsApp
          </button>
        </div>
      </div>
    </AnimatedSection>
  );
};

const BookingDetails: React.FC = () => {
  useEffect(() => {
    document.title =
      "HDT Booking — WhatsApp Ordering, Kitchen, Inventory & AI Reports";
  }, []);

  return (
    <div className="sds_bk-wrapper">
      <HeroSection />
      <ProblemSolutionSection />
      <CapabilitiesSection />
      <AISpotlightSection />
      <IndustriesSection />
      <MigrationSection />
      <PricingSection />
      <FAQSection />
      <FinalCTA />
    </div>
  );
};

export default BookingDetails;
